// GhostNode — a greyed-out, non-interactive bridge card for a manager who
// sits OUTSIDE the scoped Entra export. Rendered above the people who report
// to them so the reporting line out of the P&E org stays visible.
//
// Synthesised by `useTreeLayout` (id prefixed with GHOST_ID_PREFIX) from a
// node's `unresolvedManagerName`. Several reports can share one ghost, which
// re-unifies that sub-org. Ghosts are not clickable, not searchable, not
// counted as people, and never collapsed. See PRODOPSAI-475.

import { NODE_HEIGHT, NODE_WIDTH } from './hooks/useTreeLayout';
import { truncateLabel } from '../org-graph/utils';

/** Max graphemes for the ghost name before ellipsis — keeps long
 *  outside-export names from overflowing the fixed-width card. Matches the
 *  TreeNode budget. */
const MAX_NAME_CHARS = 22;

const RECT_RADIUS = 8;
const NAME_TEXT_Y = NODE_HEIGHT / 2 - 4;
const SUBLABEL_TEXT_Y = NODE_HEIGHT / 2 + 14;
const TEXT_PADDING = 12;
/** Dashed stroke pattern (logical units on / off) signalling "external". */
const DASH = '5 4';

interface GhostNodeProps {
  /** The unresolved manager's display name (the ghost's `displayName`). */
  name: string;
  /** Top-left x of the card (logical SVG units). */
  x: number;
  /** Top-left y of the card (logical SVG units). */
  y: number;
}

/** Stateless, non-interactive ghost-manager card. */
export function GhostNode({ name, x, y }: Readonly<GhostNodeProps>) {
  return (
    <g
      transform={`translate(${x}, ${y})`}
      data-testid="org-tree-ghost-node"
      aria-hidden="true"
    >
      <title>{`${name} — Not in P&E org`}</title>
      <rect
        x={0}
        y={0}
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
        rx={RECT_RADIUS}
        ry={RECT_RADIUS}
        fill="var(--color-muted)"
        fillOpacity={0.4}
        stroke="var(--color-muted-foreground)"
        strokeOpacity={0.5}
        strokeWidth={1.5}
        strokeDasharray={DASH}
      />
      <text
        x={TEXT_PADDING}
        y={NAME_TEXT_Y}
        fontSize={13}
        fontWeight={600}
        fill="var(--color-muted-foreground)"
      >
        {truncateLabel(name, MAX_NAME_CHARS)}
      </text>
      <text
        x={TEXT_PADDING}
        y={SUBLABEL_TEXT_Y}
        fontSize={10}
        fontStyle="italic"
        fill="var(--color-muted-foreground)"
        fillOpacity={0.8}
      >
        Not in P&amp;E org
      </text>
    </g>
  );
}
